import React from "react"

import { CRITERION_VALUE } from "@/constants/criterion-values"
import { Checkbox, Tooltip } from "@coval/ui"
import { ChevronRight } from "lucide-react"

import {
  buildHumanCriteria,
  type ExtendedGroupedSimulationData,
} from "./SimulationReviewRow"

interface ExpectedBehaviorComparisonRow {
  data: ExtendedGroupedSimulationData
  hasConflict: boolean
}

interface ExpectedBehaviorComparisonListProps {
  rows: ExpectedBehaviorComparisonRow[]
  expectedBehaviors: string[]
  canRunEvaluations: boolean
  evaluatingIds: Set<string>
  headerCheckedState: boolean | "indeterminate"
  runIdSet: Set<string>
  atCapacity: boolean
  includeOldVersions: boolean
  onToggleSelectAll: () => void
  onToggleRow: (id: string) => void
  onOpenRow: (id: string) => void
  onPointerEnter: (id: string) => void
  onPointerLeave: (id: string) => void
  isActive: (id: string) => boolean
}

type BehaviorResult = string | undefined
interface NamedBehaviorResult {
  criterion: string
  result: string
}

const RESULT_APPEARANCE: Record<string, { label: string; className: string }> =
  {
    [CRITERION_VALUE.MET]: {
      label: "Met",
      className: "bg-coval-success-text",
    },
    [CRITERION_VALUE.NOT_MET]: {
      label: "Not met",
      className: "bg-coval-danger-text",
    },
    [CRITERION_VALUE.UNKNOWN]: {
      label: "Unknown",
      className: "bg-coval-warning-text",
    },
  }

const EMPTY_APPEARANCE = {
  label: "No result",
  className: "border border-coval-border bg-background",
}

function alignResultsToBehaviors(
  behaviors: string[],
  results: NamedBehaviorResult[],
): BehaviorResult[] {
  const normalize = (value: string) => value.trim().replace(/\s+/g, " ")
  const remaining = results.map((result) => ({
    ...result,
    normalizedCriterion: normalize(result.criterion),
  }))

  return behaviors.map((behavior) => {
    const resultIndex = remaining.findIndex(
      ({ normalizedCriterion }) => normalizedCriterion === normalize(behavior),
    )
    if (resultIndex === -1) {
      return undefined
    }
    return remaining.splice(resultIndex, 1)[0].result
  })
}

function BehaviorDot({
  behavior,
  index,
  result,
}: {
  behavior: string
  index: number
  result: BehaviorResult
}) {
  const status =
    (result ? RESULT_APPEARANCE[result] : undefined) ?? EMPTY_APPEARANCE

  return (
    <Tooltip
      side="top"
      className="max-w-xs"
      content={
        <div>
          <p className="font-medium">
            Behavior {index + 1} · {status.label}
          </p>
          <p className="mt-1 text-coval-text-muted">{behavior}</p>
        </div>
      }
    >
      <span
        role="img"
        aria-label={`Behavior ${index + 1}: ${status.label}`}
        className={`size-2.5 shrink-0 rounded-full ${status.className}`}
      />
    </Tooltip>
  )
}

function BehaviorResultStrip({
  behaviors,
  isLoading = false,
  label,
  results,
  summaryFallback,
}: {
  behaviors: string[]
  isLoading?: boolean
  label: string
  results: BehaviorResult[]
  summaryFallback?: string | null
}) {
  const visibleResults = results.slice(0, behaviors.length)
  const metCount = visibleResults.filter(
    (result) => result === CRITERION_VALUE.MET,
  ).length
  const hasResult = visibleResults.some(Boolean)

  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-1">
        <span className="truncate text-[11px] font-medium text-coval-text-muted">
          {label}
        </span>
        <span className="shrink-0 text-[11px] tabular-nums text-coval-text-muted">
          {isLoading
            ? "Testing…"
            : hasResult
              ? `${metCount}/${behaviors.length}`
              : (summaryFallback ?? "—")}
        </span>
      </div>
      <div
        className={`mt-2 flex min-h-3 flex-wrap items-center gap-1.5 ${isLoading ? "animate-pulse" : ""}`}
        aria-label={`${label} behavior results`}
      >
        {behaviors.map((behavior, index) => (
          <BehaviorDot
            key={`${index}:${behavior}`}
            behavior={behavior}
            index={index}
            result={isLoading ? undefined : results[index]}
          />
        ))}
      </div>
    </div>
  )
}

function ResultLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-coval-text-muted">
      <span>Each dot is one expected behavior</span>
      {[
        ["bg-coval-success-text", "Met"],
        ["bg-coval-danger-text", "Not met"],
        ["border border-coval-border bg-background", "No result"],
      ].map(([className, label]) => (
        <span key={label} className="flex items-center gap-1">
          <span className={`size-2 rounded-full ${className}`} />
          {label}
        </span>
      ))}
    </div>
  )
}

export function ExpectedBehaviorComparisonList({
  rows,
  expectedBehaviors,
  canRunEvaluations,
  evaluatingIds,
  headerCheckedState,
  runIdSet,
  atCapacity,
  includeOldVersions,
  onToggleSelectAll,
  onToggleRow,
  onOpenRow,
  onPointerEnter,
  onPointerLeave,
  isActive,
}: ExpectedBehaviorComparisonListProps) {
  return (
    <div>
      <div className="sticky -top-px z-10 flex items-center gap-3 border-b border-coval-border bg-background px-4 py-2.5">
        {canRunEvaluations && (
          <Checkbox
            checked={headerCheckedState}
            onCheckedChange={onToggleSelectAll}
            aria-label="Select all conversations for testing"
          />
        )}
        <ResultLegend />
      </div>

      <div className="divide-y divide-coval-border">
        {rows.map(({ data, hasConflict }) => {
          const id = data.simulationOutputId
          const countedReview =
            data.humanReviews.find((review) => review.isCountedAsPrimary) ??
            data.humanReviews[0]
          const humanCriteria = buildHumanCriteria(
            countedReview,
            data.aiCriteria,
          )
          const newCriteria = data.mostRecentExperiment.criteriaResults ?? []
          const behaviors =
            expectedBehaviors.length > 0
              ? expectedBehaviors
              : Array.from(
                  {
                    length: Math.max(
                      data.aiCriteria.length,
                      newCriteria.length,
                      humanCriteria.length,
                    ),
                  },
                  (_, index) =>
                    data.mostRecentExperiment.criteriaResults?.[index]
                      ?.criterion ??
                    data.aiCriteria[index]?.criterion ??
                    `Behavior ${index + 1}`,
                )
          const previousResults = alignResultsToBehaviors(
            behaviors,
            data.aiCriteria,
          )
          const newResults = alignResultsToBehaviors(behaviors, newCriteria)
          const humanResults = alignResultsToBehaviors(behaviors, humanCriteria)
          const isSelected = runIdSet.has(id)
          const isEvaluating = evaluatingIds.has(id)
          const previousIsMuted = data.isStaleVersion && !includeOldVersions

          return (
            <div
              key={id}
              className={`flex items-start gap-3 px-4 py-3 transition-colors hover:bg-surface-1 ${
                hasConflict
                  ? "bg-coval-danger-surface"
                  : isActive(id)
                    ? "bg-surface-2"
                    : "bg-background"
              }`}
              data-simulation-review-id={id}
              onPointerEnter={() => onPointerEnter(id)}
              onPointerLeave={() => onPointerLeave(id)}
            >
              <div className="pt-0.5">
                {canRunEvaluations && (
                  <Checkbox
                    checked={isSelected}
                    disabled={atCapacity && !isSelected}
                    onCheckedChange={() => onToggleRow(id)}
                    aria-label={`Select conversation ${id.slice(0, 8)} for testing`}
                  />
                )}
              </div>
              <button
                type="button"
                aria-label={`Open conversation ${id.slice(0, 8)}`}
                className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active"
                onClick={() => onOpenRow(id)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-coval-text-muted">
                    {id.slice(0, 8)}
                  </span>
                  <ChevronRight className="size-4 text-coval-text-muted" />
                </div>
                <div className="mt-2 grid grid-cols-3 gap-4">
                  <div className={previousIsMuted ? "opacity-50" : undefined}>
                    <BehaviorResultStrip
                      behaviors={behaviors}
                      label="Previous"
                      results={previousResults}
                      summaryFallback={data.aiReview.value}
                    />
                  </div>
                  <BehaviorResultStrip
                    behaviors={behaviors}
                    isLoading={isEvaluating}
                    label="New test"
                    results={newResults}
                  />
                  <BehaviorResultStrip
                    behaviors={behaviors}
                    label="Human"
                    results={humanResults}
                  />
                </div>
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
